/**
 * The 90-day uptime strip — the thing people come to a status page to look at.
 *
 * ── One deliberate improvement on the form it imitates ──────────────────────
 * UptimeRobot paints one flat colour per day, so a day with four minutes of
 * downtime and a day that was down from breakfast to bedtime are the same red
 * bar. Here each bar is a **proportional stack**: the day's up, down and
 * maintenance seconds in their true ratio. A fully-up day is a solid teal bar
 * and looks exactly like the original; a partial outage shows as a red foot
 * under a teal body, sized to what actually happened.
 *
 * That is not decoration. Hue alone puts up and down at deutan ΔE 8.7 — clear
 * of the ≥8 target but not by much — so the strip carries a **second channel**
 * for the colourblind reader: segment height and position. Red is at the
 * bottom, always; a bar with anything at its foot had a bad day, whether or not
 * you can see which colour it is.
 *
 * ── Maintenance is drawn but not counted ────────────────────────────────────
 * A window's seconds occupy their share of the bar in violet, so the strip is
 * an honest picture of the day. They are absent from the percentage beside it,
 * because `uptimePercent` excludes them from both terms. The tooltip says so in
 * words rather than leaving the reader to reconcile a bar that is a third
 * violet with a figure of 100%.
 */

import { useId, useState } from 'react';
import type { DayWire } from '../api';
import { formatDayKey, humanDuration } from '../status';
import { uptimePercent, formatUptime } from '@shared/status';
import { monoPlain } from '../theme';

import { useT } from '../i18n';
const BAR_W = 3;
const GAP = 2;
const HEIGHT = 34;
/** A 3px mark is far below a comfortable pointer target, so the hit area is the
 *  full column. Per the interaction rules: hit targets bigger than the mark. */
const HIT_W = BAR_W + GAP;

/** Between stacked segments, so two fills never touch. 1px rather than the
 *  usual 2 — in a 34px bar split three ways, 2px gaps would eat a fifth of the
 *  data. */
const SEG_GAP = 1;

export interface UptimeBarsProps {
  days: DayWire[];
  /** Rendered under the strip. Omitted inside the developer tables, where the
   *  legend is shown once for the whole page rather than per row. */
  showLegend?: boolean;
  label: string;
}

interface Segment {
  y: number;
  h: number;
  fill: string;
}

/** Split one day into its drawn segments, top to bottom: up, maintenance, down.
 *
 *  Down at the foot is the load-bearing choice — it is the position channel
 *  that carries the meaning when hue cannot. */
function segmentsFor(day: DayWire): Segment[] {
  const total = day.up + day.down + day.maintenance;

  // No measurement at all. A short muted stub, centred: visibly *not* a
  // reading, rather than a full-height grey bar that reads as a fourth state.
  if (total <= 0) {
    return [{ y: HEIGHT / 2 - 2, h: 4, fill: 'var(--st-none-fill)' }];
  }

  const parts: { seconds: number; fill: string }[] = [
    { seconds: day.up, fill: 'var(--st-up-fill)' },
    { seconds: day.maintenance, fill: 'var(--st-maint-fill)' },
    { seconds: day.down, fill: 'var(--st-down-fill)' }
  ].filter((p) => p.seconds > 0);

  const gaps = SEG_GAP * (parts.length - 1);
  const drawable = HEIGHT - gaps;

  const out: Segment[] = [];
  let y = 0;
  for (const part of parts) {
    // A floor of 2px: an outage of ninety seconds in a day is a real event and
    // a sub-pixel sliver would round it away to an all-clear.
    const h = Math.max(2, (part.seconds / total) * drawable);
    out.push({ y, h, fill: part.fill });
    y += h + SEG_GAP;
  }

  // Rounding and the 2px floor can overshoot; scale back so the stack always
  // ends exactly at the baseline rather than growing past it.
  const end = y - SEG_GAP;
  if (end > HEIGHT) {
    const scale = HEIGHT / end;
    let cursor = 0;
    for (const seg of out) {
      seg.y = cursor;
      seg.h = seg.h * scale;
      cursor += seg.h + SEG_GAP * scale;
    }
  }

  return out;
}

function tooltipFor(day: DayWire): { title: string; lines: string[] } {
  const total = day.up + day.down + day.maintenance;
  if (total <= 0) {
    return { title: formatDayKey(day.day), lines: ['No data'] };
  }

  const percent = uptimePercent({
    upSeconds: day.up,
    downSeconds: day.down,
    maintenanceSeconds: day.maintenance
  });

  const lines: string[] = [];

  if (percent === null) {
    // Every measured second was maintenance. Saying "0%" or "100%" here would
    // both be inventions, so the tooltip says what actually happened.
    lines.push('Entirely scheduled maintenance.');
    lines.push('Excluded from uptime.');
  } else {
    lines.push(`${formatUptime(percent)} uptime`);
    if (day.down > 0) lines.push(`${humanDuration(day.down)} of downtime`);
    else lines.push('No downtime');
  }

  if (day.maintenance > 0 && percent !== null) {
    lines.push(`${humanDuration(day.maintenance)} maintenance (not counted)`);
  }
  if (day.avgLatencyMs !== null) lines.push(`${day.avgLatencyMs} ms average`);

  return { title: formatDayKey(day.day), lines };
}

const LEGEND: { fill: string; label: string }[] = [
  { fill: 'var(--st-up-fill)', label: 'Operational' },
  { fill: 'var(--st-down-fill)', label: 'Downtime' },
  { fill: 'var(--st-maint-fill)', label: 'Maintenance' },
  { fill: 'var(--st-none-fill)', label: 'No data' }
];

export function UptimeBars({ days, showLegend = false, label }: UptimeBarsProps) {
  const t = useT();
  const [hover, setHover] = useState<number | null>(null);
  const titleId = useId();

  const width = days.length * HIT_W - GAP;
  const active = hover !== null ? days[hover] : null;
  const tip = active ? tooltipFor(active) : null;

  const overall = uptimePercent(
    days.reduce(
      (acc, d) => ({
        upSeconds: acc.upSeconds + d.up,
        downSeconds: acc.downSeconds + d.down,
        maintenanceSeconds: acc.maintenanceSeconds + d.maintenance
      }),
      { upSeconds: 0, downSeconds: 0, maintenanceSeconds: 0 }
    )
  );

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        preserveAspectRatio="none"
        role="img"
        aria-labelledby={titleId}
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        <title id={titleId}>
          {t('uptime-bars.a11y', {
            label: label ?? '',
            days: days.length,
            uptime: formatUptime(overall)
          })}
        </title>

        {days.map((day, i) => (
          <g
            key={day.day}
            className="st-bar-hit"
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            tabIndex={-1}
          >
            {/* The pointer target: transparent, full height, full column. */}
            <rect x={i * HIT_W} y={0} width={HIT_W} height={HEIGHT} fill="transparent" />
            <g className="st-bar-col">
              {segmentsFor(day).map((seg, j) => (
                <rect
                  key={j}
                  x={i * HIT_W}
                  y={seg.y}
                  width={BAR_W}
                  height={seg.h}
                  rx={1.5}
                  fill={seg.fill}
                />
              ))}
            </g>
          </g>
        ))}
      </svg>

      {/* The axis, written out rather than ticked. Ninety unlabelled bars need
          to say what span they cover, and the overall figure belongs in the
          middle where it reads as the summary of what is above it. */}
      <div
        style={{
          ...monoPlain,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 6,
          gap: 12
        }}
      >
        <span>
          {days.length} {t('uptime-bars.daysago')}
        </span>
        <span style={{ color: 'var(--text-muted)' }}>
          {formatUptime(overall)} {t('uptime-bars.uptime')}
        </span>
        <span>{t('uptime-bars.today')}</span>
      </div>

      {showLegend && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px 16px',
            marginTop: 10,
            alignItems: 'center'
          }}
        >
          {LEGEND.map((item) => (
            <span
              key={item.label}
              style={{ ...monoPlain, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span
                aria-hidden
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: item.fill,
                  flex: '0 0 auto'
                }}
              />
              {item.label}
            </span>
          ))}
        </div>
      )}

      {tip && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: HEIGHT + 14,
            // Clamped to the strip so a tooltip on the first or last bar does
            // not hang off the card.
            left: `${Math.min(88, Math.max(12, ((hover! + 0.5) / days.length) * 100))}%`,
            transform: 'translateX(-50%)',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 10,
            boxShadow: '0 18px 44px rgba(40,28,45,.18)',
            padding: '10px 12px',
            minWidth: 190,
            zIndex: 5,
            pointerEvents: 'none'
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>
            {tip.title}
          </div>
          {tip.lines.map((line) => (
            <div key={line} style={{ ...monoPlain, fontSize: 11.5 }}>
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
